import { Accordion, Chip, TextInput } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import ActionButton from "#/components/buttons/ActionButton";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { VehicleDriver } from "#/types/vehicle";
import { ISO_WEEKDAYS, weekdayShortName } from "#/utils/dateTimeUtils";

interface Props {
  drivers: VehicleDriver[];
  onChange: (drivers: VehicleDriver[]) => void;
  /** The old free-text driver name, shown as a hint until real drivers are entered. */
  legacyDriver?: string | null;
}

const EMPTY_DRIVER: VehicleDriver = {
  name: "",
  startTime: "08:00",
  endTime: "17:00",
  workingDays: [1, 2, 3, 4, 5],
};

/**
 * The drivers of a vehicle: one row each with name, start and end of the
 * working day and the weekdays worked. Inline rows inside the accordion, as
 * the sub-client rotation targets. The routing uses these hours to know how
 * many deliveries a truck can make in a day.
 */
const DriversAccordion = ({ drivers, onChange, legacyDriver }: Props) => {
  const { t, i18n } = useTranslation();
  const { canWrite, canDelete } = usePermission();

  const handleAdd = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange([
      ...drivers,
      { ...EMPTY_DRIVER, name: drivers.length === 0 ? (legacyDriver ?? "") : "" },
    ]);
  };
  const updateRow = (index: number, patch: Partial<VehicleDriver>) =>
    onChange(drivers.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const removeRow = (index: number) => onChange(drivers.filter((_, i) => i !== index));

  return (
    <Accordion.Item value="drivers">
      <Accordion.Control>
        <div className="flex items-center gap-2">
          <span>{t("vehicles.drivers.title")}</span>
          <span className="text-xs font-normal text-gray-500">({drivers.length})</span>
          <PermissionTooltip disabled={!canWrite()}>
            <ActionButton
              color="black"
              size="xs"
              icon={IconPlus}
              disabled={!canWrite()}
              onClick={handleAdd}
            />
          </PermissionTooltip>
        </div>
      </Accordion.Control>
      <Accordion.Panel>
        <p className="mb-3 text-xs text-gray-500">{t("vehicles.drivers.hint")}</p>
        {drivers.length === 0 ? (
          <div className="text-sm text-gray-500">
            {legacyDriver
              ? t("vehicles.drivers.emptyWithLegacy", { name: legacyDriver })
              : t("vehicles.drivers.empty")}
          </div>
        ) : (
          <div className="flex w-full flex-col gap-3">
            {drivers.map((driver, index) => (
              <div
                key={index}
                className="grid w-full grid-cols-[auto_minmax(10rem,1fr)_8rem_8rem_auto_auto] items-end gap-2"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/5 font-bold text-black/70">
                  {index + 1}
                </span>
                <TextInput
                  label={t("vehicles.drivers.name")}
                  placeholder={t("vehicles.drivers.namePlaceholder")}
                  disabled={!canWrite()}
                  value={driver.name}
                  error={driver.name.trim() === "" ? t("vehicles.drivers.nameRequired") : undefined}
                  onChange={(event) => updateRow(index, { name: event.currentTarget.value })}
                />
                <TextInput
                  type="time"
                  label={t("vehicles.drivers.startTime")}
                  disabled={!canWrite()}
                  value={driver.startTime}
                  onChange={(event) => updateRow(index, { startTime: event.currentTarget.value })}
                />
                <TextInput
                  type="time"
                  label={t("vehicles.drivers.endTime")}
                  disabled={!canWrite()}
                  value={driver.endTime}
                  error={
                    driver.endTime <= driver.startTime
                      ? t("vehicles.drivers.endAfterStart")
                      : undefined
                  }
                  onChange={(event) => updateRow(index, { endTime: event.currentTarget.value })}
                />
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-gray-900">
                    {t("vehicles.drivers.workingDays")}
                  </span>
                  <Chip.Group
                    multiple
                    value={driver.workingDays.map(String)}
                    onChange={(values) =>
                      updateRow(index, {
                        workingDays: values.map(Number).sort((a, b) => a - b),
                      })
                    }
                  >
                    <div className="flex flex-wrap gap-1">
                      {ISO_WEEKDAYS.map((weekday) => (
                        <Chip
                          key={weekday}
                          value={String(weekday)}
                          size="xs"
                          variant="light"
                          disabled={!canWrite()}
                        >
                          {weekdayShortName(weekday, i18n.language)}
                        </Chip>
                      ))}
                    </div>
                  </Chip.Group>
                  {driver.workingDays.length === 0 && (
                    <span className="text-xs text-red-600">
                      {t("vehicles.drivers.daysRequired")}
                    </span>
                  )}
                </div>
                <PermissionTooltip disabled={!canDelete()}>
                  <ActionButton
                    icon={IconTrash}
                    variant="white"
                    color="red.5"
                    disabled={!canDelete()}
                    onClick={() => removeRow(index)}
                  />
                </PermissionTooltip>
              </div>
            ))}
          </div>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export default DriversAccordion;
